import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import userInfo from 'utils/userInfo'
import {
  ChangePassword,
  ChangePin,
  ChangeProfileImage,
  checkPinCode,
} from 'service/AuthService'
import {
  changeImageAvatar,
  changePassword,
  changePinCode,
} from 'store/slices/UserSlice'
import DynamicUserInfo from 'utils/DynamicUserInfo'
import Notification from 'utils/Notifications'

import ProfileTopBar from './components/ProfileTopBar/ProfileTopBar'
import ProfileAvatar from './components/ProfileAvatar/ProfileAvatar'
import ProfileInfo from './components/ProfileInfo/ProfileInfo'
import ProfilePinModals from './components/ProfilePinModals/ProfilePinModals'
import ProfileOptionsModal from './components/ProfileOptionsModal/ProfileOptionsModal'
import styles from './ProfilePage.module.scss'

function ProfilePage({ userId }) {
  const { t } = useTranslation('common')
  // Wave 4: title в табе браузера для страницы профиля.
  useEffect(() => {
    document.title = `${t('profile.title', 'Профиль')} — ЧУ`
  }, [t])
  const [openModal, setOpenModal] = useState(false)
  const [openModalOption, setOpenModalOption] = useState(false)
  const [openModalPassword, setOpenModalPassword] = useState(false)
  const [verifyPin, setVerifyPin] = useState(false)
  const [modalText, setModalText] = useState(t('profile.pin.enter_old_pin'))
  const [isShown, setIsShown] = useState(false)
  const [isShownImg, setIsShownImg] = useState(false)
  const [pinCode, setPinCode] = useState()
  const [file, setFile] = useState('')
  const [password, setPassword] = useState('')
  const [currentDynamicUser, getDynamic] = useState()
  const [isExpanded, setIsExpanded] = useState(false)

  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [notify, setNotify] = useState({ isOpen: false, message: '', type: '' })
  const user = userInfo()

  const changePinValidation = async () => {
    if (!pinCode || String(pinCode).length < 4) {
      setNotify({ isOpen: true, message: t('profile.pin.enter_pin'), type: 'error', sound: 'error' })
      return
    }
    if (String(pinCode).length === 4) {
      try {
        const response = await checkPinCode(pinCode, userId)
        if (response.data.success) {
          setNotify({ isOpen: true, message: t('profile.pin.pin_confirmed'), type: 'success', sound: 'success' })
          setModalText(t('profile.pin.enter_new_pin'))
          setVerifyPin(true)
        } else {
          setNotify({ isOpen: true, message: t('profile.pin.wrong_pin'), type: 'error', sound: 'error' })
        }
      } catch (error) {
        setNotify({ isOpen: true, message: t('common.server_error_retry'), type: 'error', sound: 'error' })
      }
    }
  }

  const changePasswordValidation = async () => {
    if (!pinCode || String(pinCode).length < 4) {
      setNotify({ isOpen: true, message: t('profile.pin.enter_pin'), type: 'error', sound: 'error' })
      return
    }
    try {
      const response = await checkPinCode(pinCode, user.id)
      if (response.data.success) {
        setNotify({ isOpen: true, message: t('profile.pin.pin_confirmed'), type: 'success', sound: 'success' })
        setIsShown(true)
        setOpenModalPassword(false)
      } else {
        setNotify({ isOpen: true, message: t('profile.pin.wrong_pin'), type: 'error', sound: 'error' })
      }
    } catch (error) {
      setNotify({ isOpen: true, message: t('profile.pin.pin_check_error'), type: 'error', sound: 'error' })
    }
  }

  const handleClick = () => {
    setIsShown(false)
    setIsShownImg(false)
  }

  const onFileChange = (e) => {
    setFile(e.target.files[0])
  }

  const ChangeImage = async ({ file }) => {
    // Wave Final (P1-F14): без reload — dispatch(changeImageAvatar) уже
    // обновил аватар в store, остальные компоненты пересобираются сами.
    let response = await ChangeProfileImage(file)
    dispatch(changeImageAvatar(response.data))
    setNotify({ isOpen: true, message: t('profile.photo_updated'), type: 'success', sound: 'success' })
  }

  const createUserPin = async () => {
    try {
      await ChangePin(pinCode)
      dispatch(changePinCode({ pin: pinCode }))
      setNotify({ isOpen: true, message: t('profile.pin.pin_changed'), type: 'success', sound: 'success' })
      setTimeout(() => {
        setOpenModal(false)
      }, 1000)
      localStorage.removeItem('token')
      localStorage.removeItem('task')
      localStorage.removeItem('user')
      navigate('/')
    } catch (error) {
      setNotify({ isOpen: true, message: t('common.error'), type: 'error', sound: 'error' })
    }
  }

  const handleSubmit = async (event) => {
    if (event && event.preventDefault) event.preventDefault()
    if (password === '') {
      setNotify({ isOpen: true, message: t('profile.password.no_new_password'), type: 'error', sound: 'error' })
    } else
      try {
        await ChangePassword(password)
        dispatch(changePassword({ new_password: password }))
        setNotify({ isOpen: true, message: t('profile.password.password_changed'), type: 'success', sound: 'success' })
        // Wave Final (P1-F14): после смены пароля backend инвалидирует
        // refresh-token; редирект на login безопаснее, чем reload (cleared state).
        setTimeout(() => {
          localStorage.removeItem('token')
          localStorage.removeItem('refresh')
          navigate('/')
        }, 1000)
      } catch (error) {
        setNotify({ isOpen: true, message: t('common.error'), type: 'error', sound: 'error' })
      }
  }

  const handleClickImg = () => setIsExpanded(!isExpanded)

  const handleExit = () => {
    localStorage.removeItem('token')
    localStorage.removeItem('task')
    localStorage.removeItem('user')
    navigate('/')
  }

  return (
    <div className={styles.profile_wrapper}>
      <ProfileTopBar
        user={user}
        userId={userId}
        onOpenOptions={() => setOpenModalOption(true)}
        onExit={handleExit}
      />
      <div className={styles.both_side}>
        <div className={styles.left_side}>
          <ProfileAvatar
            src={currentDynamicUser?.imeag}
            isExpanded={isExpanded}
            onToggle={handleClickImg}
          />
        </div>
        <div className={styles.right_side}>
          <ProfileInfo user={user} currentDynamicUser={currentDynamicUser} />
          <ProfilePinModals
            openModalPassword={openModalPassword}
            setOpenModalPassword={setOpenModalPassword}
            openModal={openModal}
            setOpenModal={setOpenModal}
            modalText={modalText}
            verifyPin={verifyPin}
            setPinCode={setPinCode}
            onPasswordValidate={changePasswordValidation}
            onPinValidate={changePinValidation}
            onCreateUserPin={createUserPin}
          />
        </div>
        <DynamicUserInfo
          idEmployee={userId === undefined ? user.userId : userId}
          getDynamic={getDynamic}
        />
        <Notification notify={notify} setNotify={setNotify} />
        <ProfileOptionsModal
          openModalOption={openModalOption}
          setOpenModalOption={setOpenModalOption}
          isShown={isShown}
          isShownImg={isShownImg}
          setIsShownImg={setIsShownImg}
          setPassword={setPassword}
          file={file}
          onFileChange={onFileChange}
          onChangeImage={ChangeImage}
          onOpenPassword={() => setOpenModalPassword(true)}
          onSubmitPassword={handleSubmit}
          onOpenPin={() => setOpenModal(true)}
          onCloseAll={handleClick}
          userId={userId}
        />
      </div>
    </div>
  )
}

export default ProfilePage
